import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  ReDebtPaydownModal,
  ReDebtRateResetModal,
  ReDebtAmendmentModal,
  ReDebtWritedownModal,
  ReDebtPayoffModal,
  ReDebtConstructionDrawModal,
  ReDebtPositionLite,
} from '../realestate/ReDebtActionModals';
import RealEstateRefiRiskPanel from './RealEstateRefiRiskPanel';

const Container = styled.div<{ $embedded?: boolean }>`
  max-width: 1800px;
  margin: 0 auto;
  padding: ${props => props.$embedded ? '1rem' : '2rem'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #ec4899 0%, #db2777 100%);
    color: white;
    border: none;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(236, 72, 153, 0.4); }
  ` : `
    background: transparent;
    color: #94a3b8;
    border: 1px solid rgba(255, 255, 255, 0.1);
    &:hover { border-color: #ec4899; color: white; }
  `}
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div<{ $color?: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  border-left: 4px solid ${props => props.$color || '#ec4899'};
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
`;

const TabsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? 'rgba(236, 72, 153, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.$active ? '#ec4899' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$active ? 'white' : '#94a3b8'};

  &:hover {
    border-color: #ec4899;
    color: white;
  }
`;

const ChipRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;
`;

const ChipLabel = styled.span`
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-right: 0.25rem;
`;

const Chip = styled.button<{ $active: boolean }>`
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? 'rgba(99, 102, 241, 0.25)' : 'transparent'};
  border: 1px solid ${props => props.$active ? '#6366f1' : 'rgba(255, 255, 255, 0.12)'};
  color: ${props => props.$active ? 'white' : '#94a3b8'};
  &:hover { border-color: #6366f1; color: white; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(30, 41, 59, 0.5);
  border-radius: 12px;
  overflow: hidden;
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  background: rgba(30, 41, 59, 0.8);
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  padding: 1rem;
  color: white;
  font-size: 0.875rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;

  ${props => {
    switch (props.$status) {
      case 'PERFORMING':
        return 'background: rgba(16, 185, 129, 0.1); color: #10b981; border: 1px solid rgba(16, 185, 129, 0.2);';
      case 'WATCHLIST':
        return 'background: rgba(245, 158, 11, 0.1); color: #f59e0b; border: 1px solid rgba(245, 158, 11, 0.2);';
      case 'DELINQUENT':
        return 'background: rgba(239, 68, 68, 0.1); color: #ef4444; border: 1px solid rgba(239, 68, 68, 0.2);';
      case 'CONSTRUCTION':
        return 'background: rgba(59, 130, 246, 0.1); color: #3b82f6; border: 1px solid rgba(59, 130, 246, 0.2);';
      default:
        return 'background: rgba(148, 163, 184, 0.1); color: #94a3b8; border: 1px solid rgba(148, 163, 184, 0.2);';
    }
  }}
`;

const PropertyTypeBadge = styled.span<{ $type: string }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;

  ${props => {
    switch (props.$type) {
      case 'OFFICE':
        return 'background: rgba(59, 130, 246, 0.1); color: #3b82f6;';
      case 'MULTIFAMILY':
        return 'background: rgba(16, 185, 129, 0.1); color: #10b981;';
      case 'INDUSTRIAL':
        return 'background: rgba(139, 92, 246, 0.1); color: #8b5cf6;';
      case 'RETAIL':
        return 'background: rgba(245, 158, 11, 0.1); color: #f59e0b;';
      case 'HOSPITALITY':
        return 'background: rgba(236, 72, 153, 0.1); color: #ec4899;';
      default:
        return 'background: rgba(148, 163, 184, 0.1); color: #94a3b8;';
    }
  }}
`;

const ClassChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const ClassChip = styled.span<{ $tone?: 'debt' | 'equity' | 'mezz' | 'pik' | 'hybrid' | 'neutral' }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  ${props => {
    switch (props.$tone) {
      case 'equity':  return 'background: rgba(236, 72, 153, 0.12); color: #f472b6;';
      case 'mezz':    return 'background: rgba(245, 158, 11, 0.12); color: #fbbf24;';
      case 'pik':     return 'background: rgba(168, 85, 247, 0.12); color: #c084fc;';
      case 'hybrid':  return 'background: rgba(14, 165, 233, 0.12); color: #38bdf8;';
      case 'debt':    return 'background: rgba(16, 185, 129, 0.10); color: #34d399;';
      default:        return 'background: rgba(148, 163, 184, 0.10); color: #94a3b8;';
    }
  }}
`;

const MetricIndicator = styled.span<{ $level: 'safe' | 'warning' | 'danger' }>`
  font-weight: 600;
  color: ${props => {
    switch (props.$level) {
      case 'safe': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'danger': return '#ef4444';
    }
  }};
`;

const ProgressBar = styled.div`
  width: 80px;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number; $color: string }>`
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${props => props.$color};
  border-radius: 4px;
`;

interface RealEstateLoan {
  id: number;
  loanName: string;
  propertyName: string;
  propertyType: string;
  status: string;
  originalAmount: number;
  currentPrincipal: number;
  fundedAmount?: number;
  unfundedCommitment: number;
  currentLtv: number;
  maxLtv: number;
  maxLtvCovenant?: number;
  currentDscr: number;
  minDscr: number;
  minDscrCovenant?: number;
  interestRate: number;
  spreadBps?: number;
  indexRate?: string;
  rateType?: string;
  maturityDate: string;
  state: string;
  city: string;
  isConstruction: boolean;
  completionPercent: number;
  appraisedValue?: number;
  // V295/V296 — subtype + mezz/construction surfacing
  loanType?: string;
  instrumentSubtype?: string;
  pikAccruedBalance?: number;
  // V299 — typed classification discriminators
  collateralType?: string;
  gaapClassification?: string;
  payMechanic?: string;
}

type ActionKind = 'paydown' | 'rate-reset' | 'amendment' | 'writedown' | 'payoff' | 'draw';

type SubtypeFilter = 'ALL' | 'WHOLE_LOAN' | 'MEZZANINE' | 'BRIDGE' | 'CONSTRUCTION' | 'PREFERRED_EQUITY';

const RowActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ActionBtn = styled.button<{ $color?: string }>`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.$color || 'rgba(139, 92, 246, 0.15)'};
  color: white;
  border: 1px solid ${p => p.$color ? 'transparent' : 'rgba(139, 92, 246, 0.4)'};
  transition: filter 0.15s;
  &:hover { filter: brightness(1.15); }
`;

interface RealEstateDebtScreenProps {
  embedded?: boolean;
}

const RealEstateDebtScreen: React.FC<RealEstateDebtScreenProps> = ({ embedded = false }) => {
  const [loans, setLoans] = useState<RealEstateLoan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'performing' | 'watchlist' | 'construction' | 'refiRisk'>('all');
  const [subtypeFilter, setSubtypeFilter] = useState<SubtypeFilter>('ALL');
  const [activeModal, setActiveModal] = useState<{ kind: ActionKind; loan: RealEstateLoan } | null>(null);

  const fetchLoans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/credit/real-estate');
      const data = response.data;
      setLoans(Array.isArray(data) ? data : (data?.loans || data?.content || []));
    } catch (err) {
      console.error('Failed to fetch real estate loans:', err);
      setError('Failed to load real estate loans. Please try again.');
      setLoans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  const closeModalAndRefresh = useCallback(() => {
    setActiveModal(null);
    fetchLoans();
  }, [fetchLoans]);

  const toLite = (loan: RealEstateLoan): ReDebtPositionLite => ({
    id: loan.id,
    loanName: loan.loanName,
    propertyName: loan.propertyName,
    propertyType: loan.propertyType,
    status: loan.status,
    currentPrincipal: loan.currentPrincipal,
    fundedAmount: loan.fundedAmount ?? loan.currentPrincipal,
    interestRate: loan.interestRate,
    spreadBps: loan.spreadBps,
    indexRate: loan.indexRate,
    rateType: loan.rateType,
    maturityDate: loan.maturityDate,
    maxLtvCovenant: loan.maxLtvCovenant ?? loan.maxLtv,
    minDscrCovenant: loan.minDscrCovenant ?? loan.minDscr,
    appraisedValue: loan.appraisedValue,
  });

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '$0';
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(2)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    return `$${value.toLocaleString()}`;
  };

  const getLtvLevel = (current: number | undefined | null, max: number | undefined | null): 'safe' | 'warning' | 'danger' => {
    const c = current || 0;
    const m = max || 100;
    if (c > m) return 'danger';
    if (c >= m - 5) return 'warning';
    return 'safe';
  };

  const getDscrLevel = (current: number | undefined | null, min: number | undefined | null): 'safe' | 'warning' | 'danger' => {
    const c = current || 0;
    const m = min || 0;
    if (c < m) return 'danger';
    if (c < m + 0.1) return 'warning';
    return 'safe';
  };

  const safeLoans = Array.isArray(loans) ? loans : [];

  const matchesSubtype = (loan: RealEstateLoan): boolean => {
    if (subtypeFilter === 'ALL') return true;
    const subtype = loan.instrumentSubtype || '';
    const lt = (loan.loanType || '').toUpperCase();
    if (subtypeFilter === 'BRIDGE') return subtype === 'BRIDGE_SENIOR' || subtype === 'BRIDGE_MEZZ' || lt === 'BRIDGE';
    if (subtypeFilter === 'WHOLE_LOAN') return subtype === 'WHOLE_LOAN' || subtype === '' || lt === 'SENIOR' || lt === 'PERMANENT';
    if (subtypeFilter === 'CONSTRUCTION') return subtype === 'CONSTRUCTION' || lt === 'CONSTRUCTION' || loan.isConstruction;
    return subtype === subtypeFilter || lt === subtypeFilter;
  };

  const filteredLoans = safeLoans.filter(loan => {
    if (!matchesSubtype(loan)) return false;
    if (activeTab === 'all') return true;
    if (activeTab === 'performing') return loan.status === 'PERFORMING';
    if (activeTab === 'watchlist') return loan.status === 'WATCHLIST' || loan.status === 'DELINQUENT';
    if (activeTab === 'construction') return loan.isConstruction;
    return true;
  });

  const subtypeCount = (key: SubtypeFilter): number =>
    safeLoans.filter(l => {
      const subtype = l.instrumentSubtype || '';
      const lt = (l.loanType || '').toUpperCase();
      if (key === 'ALL') return true;
      if (key === 'BRIDGE') return subtype === 'BRIDGE_SENIOR' || subtype === 'BRIDGE_MEZZ' || lt === 'BRIDGE';
      if (key === 'WHOLE_LOAN') return subtype === 'WHOLE_LOAN' || subtype === '' || lt === 'SENIOR' || lt === 'PERMANENT';
      if (key === 'CONSTRUCTION') return subtype === 'CONSTRUCTION' || lt === 'CONSTRUCTION' || l.isConstruction;
      return subtype === key || lt === key;
    }).length;

  const totalOutstanding = safeLoans.reduce((sum, l) => sum + (l.currentPrincipal || 0), 0);
  const totalUnfunded = safeLoans.reduce((sum, l) => sum + (l.unfundedCommitment || 0), 0);
  const watchlistCount = safeLoans.filter(l => l.status === 'WATCHLIST' || l.status === 'DELINQUENT').length;
  const constructionCount = safeLoans.filter(l => l.isConstruction).length;

  return (
    <Container $embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>
            <span>🏢</span>
            Real Estate Debt
          </Title>
          <Actions>
            <Button $variant="secondary">Export</Button>
            <Button $variant="primary">+ New Loan</Button>
          </Actions>
        </Header>
      )}

      {error && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#ef4444'
        }}>
          {error}
        </div>
      )}

      <StatsRow>
        <StatCard $color="#ec4899">
          <StatLabel>Outstanding</StatLabel>
          <StatValue>{formatCurrency(totalOutstanding)}</StatValue>
        </StatCard>
        <StatCard $color="#3b82f6">
          <StatLabel>Unfunded</StatLabel>
          <StatValue>{formatCurrency(totalUnfunded)}</StatValue>
        </StatCard>
        <StatCard $color="#10b981">
          <StatLabel>Total Loans</StatLabel>
          <StatValue>{safeLoans.length}</StatValue>
        </StatCard>
        <StatCard $color="#f59e0b">
          <StatLabel>Watchlist</StatLabel>
          <StatValue>{watchlistCount}</StatValue>
        </StatCard>
        <StatCard $color="#8b5cf6">
          <StatLabel>Construction</StatLabel>
          <StatValue>{constructionCount}</StatValue>
        </StatCard>
      </StatsRow>

      <TabsRow>
        <TabButton $active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
          All ({safeLoans.length})
        </TabButton>
        <TabButton $active={activeTab === 'performing'} onClick={() => setActiveTab('performing')}>
          Performing ({safeLoans.filter(l => l.status === 'PERFORMING').length})
        </TabButton>
        <TabButton $active={activeTab === 'watchlist'} onClick={() => setActiveTab('watchlist')}>
          Watchlist ({watchlistCount})
        </TabButton>
        <TabButton $active={activeTab === 'construction'} onClick={() => setActiveTab('construction')}>
          Construction ({constructionCount})
        </TabButton>
        <TabButton $active={activeTab === 'refiRisk'} onClick={() => setActiveTab('refiRisk')}>
          Refi Risk + Caps
        </TabButton>
      </TabsRow>

      {activeTab === 'refiRisk' && <RealEstateRefiRiskPanel />}

      {activeTab !== 'refiRisk' && (<>
      <ChipRow>
        <ChipLabel>Subtype</ChipLabel>
        <Chip $active={subtypeFilter === 'ALL'} onClick={() => setSubtypeFilter('ALL')}>
          All ({subtypeCount('ALL')})
        </Chip>
        <Chip $active={subtypeFilter === 'WHOLE_LOAN'} onClick={() => setSubtypeFilter('WHOLE_LOAN')}>
          Whole Loan ({subtypeCount('WHOLE_LOAN')})
        </Chip>
        <Chip $active={subtypeFilter === 'MEZZANINE'} onClick={() => setSubtypeFilter('MEZZANINE')}>
          Mezzanine ({subtypeCount('MEZZANINE')})
        </Chip>
        <Chip $active={subtypeFilter === 'BRIDGE'} onClick={() => setSubtypeFilter('BRIDGE')}>
          Bridge ({subtypeCount('BRIDGE')})
        </Chip>
        <Chip $active={subtypeFilter === 'CONSTRUCTION'} onClick={() => setSubtypeFilter('CONSTRUCTION')}>
          Construction ({subtypeCount('CONSTRUCTION')})
        </Chip>
        <Chip $active={subtypeFilter === 'PREFERRED_EQUITY'} onClick={() => setSubtypeFilter('PREFERRED_EQUITY')}>
          Pref Equity ({subtypeCount('PREFERRED_EQUITY')})
        </Chip>
      </ChipRow>

      <Table>
        <thead>
          <tr>
            <Th>Property</Th>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>Outstanding</Th>
            <Th>LTV</Th>
            <Th>DSCR</Th>
            <Th>Rate</Th>
            <Th>Maturity</Th>
            <Th>Location</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {filteredLoans.map(loan => (
            <tr key={loan.id}>
              <Td>
                <div style={{ fontWeight: 600 }}>{loan.propertyName || 'Unknown Property'}</div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{loan.loanName || '-'}</div>
                {(loan.collateralType || loan.gaapClassification || loan.payMechanic) && (
                  <ClassChipRow>
                    {loan.collateralType && (
                      <ClassChip
                        title="Collateral type"
                        $tone={loan.collateralType.includes('EQUITY') || loan.collateralType.includes('OPERATING_AGREEMENT')
                          ? 'equity'
                          : loan.collateralType === 'INTERCREDITOR_SUBORDINATED'
                            ? 'mezz' : 'neutral'}
                      >
                        {loan.collateralType.replace(/_/g, ' ')}
                      </ClassChip>
                    )}
                    {loan.gaapClassification && loan.gaapClassification !== 'DEBT' && (
                      <ClassChip
                        title="GAAP classification"
                        $tone={loan.gaapClassification === 'PERMANENT_EQUITY' ? 'equity' : 'mezz'}
                      >
                        {loan.gaapClassification.replace(/_/g, ' ')}
                      </ClassChip>
                    )}
                    {loan.payMechanic && loan.payMechanic !== 'CASH_PAY' && (
                      <ClassChip
                        title="Pay mechanic"
                        $tone={loan.payMechanic === 'HYBRID' ? 'hybrid' : 'pik'}
                      >
                        {loan.payMechanic.replace(/_/g, ' ')}
                      </ClassChip>
                    )}
                  </ClassChipRow>
                )}
              </Td>
              <Td><PropertyTypeBadge $type={loan.propertyType || 'UNKNOWN'}>{loan.propertyType || 'Unknown'}</PropertyTypeBadge></Td>
              <Td>
                <StatusBadge $status={loan.status || 'UNKNOWN'}>{loan.status || 'Unknown'}</StatusBadge>
                {loan.isConstruction && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <ProgressBar>
                      <ProgressFill $percent={loan.completionPercent || 0} $color="#3b82f6" />
                    </ProgressBar>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{loan.completionPercent || 0}%</span>
                  </div>
                )}
              </Td>
              <Td>
                <div>{formatCurrency(loan.currentPrincipal)}</div>
                {(loan.unfundedCommitment || 0) > 0 && (
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>+{formatCurrency(loan.unfundedCommitment)} unfunded</div>
                )}
              </Td>
              <Td>
                {(loan.currentLtv || 0) > 0 ? (
                  <MetricIndicator $level={getLtvLevel(loan.currentLtv, loan.maxLtv)}>
                    {loan.currentLtv || 0}% / {loan.maxLtv || 0}%
                  </MetricIndicator>
                ) : (
                  <span style={{ color: '#64748b' }}>N/A</span>
                )}
              </Td>
              <Td>
                {(loan.currentDscr || 0) > 0 ? (
                  <MetricIndicator $level={getDscrLevel(loan.currentDscr, loan.minDscr)}>
                    {(loan.currentDscr || 0).toFixed(2)}x / {(loan.minDscr || 0).toFixed(2)}x
                  </MetricIndicator>
                ) : (
                  <span style={{ color: '#64748b' }}>N/A</span>
                )}
              </Td>
              <Td>{(loan.interestRate || 0).toFixed(2)}%</Td>
              <Td>{loan.maturityDate ? new Date(loan.maturityDate).toLocaleDateString() : '-'}</Td>
              <Td>{loan.city || '-'}, {loan.state || '-'}</Td>
              <Td>
                <RowActions>
                  {(loan.isConstruction
                      || loan.instrumentSubtype === 'CONSTRUCTION'
                      || (loan.loanType || '').toUpperCase() === 'CONSTRUCTION') && (
                    <ActionBtn
                      $color="rgba(99, 102, 241, 0.25)"
                      title="Fund a construction draw"
                      onClick={() => setActiveModal({ kind: 'draw', loan })}
                    >
                      Draw
                    </ActionBtn>
                  )}
                  <ActionBtn
                    $color="rgba(16, 185, 129, 0.25)"
                    title="Record principal paydown"
                    onClick={() => setActiveModal({ kind: 'paydown', loan })}
                  >
                    Paydown
                  </ActionBtn>
                  {(loan.rateType === 'FLOATING' || !loan.rateType) && (
                    <ActionBtn
                      $color="rgba(59, 130, 246, 0.25)"
                      title="Reset floating-rate index/spread"
                      onClick={() => setActiveModal({ kind: 'rate-reset', loan })}
                    >
                      Rate Reset
                    </ActionBtn>
                  )}
                  <ActionBtn
                    $color="rgba(139, 92, 246, 0.25)"
                    title="Amend maturity / covenants / extension options"
                    onClick={() => setActiveModal({ kind: 'amendment', loan })}
                  >
                    Amend
                  </ActionBtn>
                  <ActionBtn
                    $color="rgba(245, 158, 11, 0.25)"
                    title="Mark down carry value (impairment)"
                    onClick={() => setActiveModal({ kind: 'writedown', loan })}
                  >
                    Writedown
                  </ActionBtn>
                  <ActionBtn
                    $color="rgba(236, 72, 153, 0.25)"
                    title="Full payoff — close position"
                    onClick={() => setActiveModal({ kind: 'payoff', loan })}
                  >
                    Payoff
                  </ActionBtn>
                </RowActions>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      </>)}

      {activeModal?.kind === 'paydown' && (
        <ReDebtPaydownModal
          position={toLite(activeModal.loan)}
          onClose={() => setActiveModal(null)}
          onSuccess={closeModalAndRefresh}
        />
      )}
      {activeModal?.kind === 'rate-reset' && (
        <ReDebtRateResetModal
          position={toLite(activeModal.loan)}
          onClose={() => setActiveModal(null)}
          onSuccess={closeModalAndRefresh}
        />
      )}
      {activeModal?.kind === 'amendment' && (
        <ReDebtAmendmentModal
          position={toLite(activeModal.loan)}
          onClose={() => setActiveModal(null)}
          onSuccess={closeModalAndRefresh}
        />
      )}
      {activeModal?.kind === 'writedown' && (
        <ReDebtWritedownModal
          position={toLite(activeModal.loan)}
          onClose={() => setActiveModal(null)}
          onSuccess={closeModalAndRefresh}
        />
      )}
      {activeModal?.kind === 'payoff' && (
        <ReDebtPayoffModal
          position={toLite(activeModal.loan)}
          onClose={() => setActiveModal(null)}
          onSuccess={closeModalAndRefresh}
        />
      )}
      {activeModal?.kind === 'draw' && (
        <ReDebtConstructionDrawModal
          position={toLite(activeModal.loan)}
          unfundedCommitment={activeModal.loan.unfundedCommitment}
          onClose={() => setActiveModal(null)}
          onSuccess={closeModalAndRefresh}
        />
      )}
    </Container>
  );
};

export default RealEstateDebtScreen;
